/**
 * Routes display network payloads to provider-specific adapters.
 */
import {
  ensureCatholicAdnetScript,
  mountCatholicAdnetUnit,
} from "./CatholicAdnetAdapter";
import {
  ensureHouseAdsScript,
  mountHouseAdsUnit,
} from "./HouseAdsAdapter";
import {
  detectDeviceClass,
  selectConfiguredUnit,
} from "./selectConfiguredUnit";

export { detectDeviceClass, selectConfiguredUnit };
export {
  DISPLAY_NO_FILL_TIMEOUT_MS,
  findDisplayCreative,
  watchDisplayCreative,
} from "./displayCreativeWatcher";

var displayAdapters = {
  catholicadnet: {
    ensureScript: ensureCatholicAdnetScript,
    mountUnit: mountCatholicAdnetUnit,
    selectUnit: selectConfiguredUnit,
  },
  house: {
    ensureScript: ensureHouseAdsScript,
    mountUnit: mountHouseAdsUnit,
    selectUnit: selectConfiguredUnit,
  },
};

/**
 * @param {string} provider
 * @returns {object|null}
 */
export function getDisplayAdapter(provider) {
  if (!provider) return null;
  return displayAdapters[String(provider)] || null;
}

/**
 * @param {object} network
 * @param {object} context
 * @returns {object}
 */
export function selectDisplayUnit(network, context) {
  if (!network) return network;
  var adapter = getDisplayAdapter(network.provider);
  if (adapter && typeof adapter.selectUnit === "function") {
    return adapter.selectUnit(network, context || {});
  }
  return selectConfiguredUnit(network, context || {});
}

/**
 * @param {object} network
 * @returns {boolean}
 */
export function isDisplayNetworkItem(network) {
  // Truthy display — tolerate JSON/boolean coercion from feed payloads.
  return !!(network && network.display && network.provider);
}

/**
 * @param {object} network
 * @returns {Promise<HTMLElement|null>}
 */
export function loadDisplayNetwork(network) {
  if (!isDisplayNetworkItem(network)) {
    return Promise.resolve(null);
  }
  var selected = selectDisplayUnit(network, { device: detectDeviceClass() });
  var adapter = getDisplayAdapter(selected.provider);
  if (!adapter) {
    return Promise.resolve(null);
  }
  return adapter.ensureScript(selected.loader_url).then(function () {
    var host = document.createElement("div");
    host.className = "display-ad__host";
    return adapter.mountUnit(host, selected) ? host : null;
  });
}

/**
 * Warm the provider loader for a slot that may render soon. Loads the
 * adapter's (singleton, deduplicated) script only — never mounts a unit or
 * issues a bid. Failures are swallowed: the cached promise is dropped by the
 * adapter, so the real render retries and reports its own failure / no-fill.
 *
 * @param {object} network
 * @returns {Promise<void>}
 */
export function prepareDisplayNetwork(network) {
  if (!isDisplayNetworkItem(network)) return Promise.resolve();
  var selected = selectDisplayUnit(network, {
    device: (network && network.device_class) || detectDeviceClass(),
  });
  var adapter = getDisplayAdapter(selected && selected.provider);
  if (!adapter || !selected.loader_url) return Promise.resolve();
  try {
    return Promise.resolve(adapter.ensureScript(selected.loader_url)).then(
      function () {},
      function () {}
    );
  } catch (e) {
    return Promise.resolve();
  }
}

/**
 * @param {HTMLElement} container
 * @param {object} network
 * @returns {Promise<HTMLElement|null>}
 */
export function renderDisplayNetwork(container, network, options) {
  if (!container || !isDisplayNetworkItem(network)) {
    return Promise.resolve(null);
  }
  var opts = options || {};
  var selected = selectDisplayUnit(network, {
    device: (network && network.device_class) || detectDeviceClass(),
  });
  var adapter = getDisplayAdapter(selected.provider);
  if (!adapter) {
    return Promise.resolve(null);
  }
  var mountNetwork = Object.assign({}, selected, {
    placement_key:
      opts.placement_key ||
      opts.placementKey ||
      network.placement_key ||
      network.placementKey ||
      "",
  });
  return adapter.ensureScript(mountNetwork.loader_url).then(function () {
    return adapter.mountUnit(container, mountNetwork) ? container : null;
  });
}
